import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button, Input, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { Search, Send, RefreshCw } from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { useProspectAuth as useAuth } from '../../Auth/ProspectAuthContext';
import { alertErro, alertConfirmar } from '../../utils/alerts';

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#7366ff', '#54ba4a', '#16C7F9', '#FFAA05', '#f73164'];

function avatarColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function initials(name, phone) {
  if (name) return name.slice(0, 2).toUpperCase();
  return phone ? phone.slice(-2) : '??';
}

function lastMsg(messages) {
  if (!Array.isArray(messages) || !messages.length) return '—';
  const last = messages[messages.length - 1];
  const preview = last?.content || '';
  return preview.length > 60 ? preview.slice(0, 60) + '…' : preview;
}

function formatMsgTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const TEMP_CONFIG = {
  frio:   { label: '🧊', bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  morno:  { label: '🌤️', bg: '#fff8e1', color: '#e65100', border: '#ffcc80' },
  quente: { label: '🔥', bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
};

// ── Componente principal ──────────────────────────────────────────────────────

const TABS = [
  { key: 'new',    label: 'Novos'  },
  { key: 'mine',   label: 'Meus'   },
  { key: 'others', label: 'Outros' },
];

export default function Conversas() {
  const { user }              = useAuth();
  const userEmail = user?.email || '';
  const isAdmin   = user?.role === 'admin';

  const [tab, setTab]         = useState('new');
  const [search, setSearch]   = useState('');
  const [convs, setConvs]     = useState([]);
  const [activeId, setActive] = useState(null);
  const [detail, setDetail]   = useState(null);
  const [loadingList, setLL]  = useState(true);
  const [loadingDet, setLD]   = useState(false);
  const [text, setText]       = useState('');
  const [sending, setSending] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [transferTo, setTransferTo]       = useState('');
  const [updatingTemp, setUpdatingTemp]   = useState(false);

  const messagesEnd = useRef(null);
  const listTimer   = useRef(null);
  const detTimer    = useRef(null);
  const replyRef    = useRef(null);

  // ── Fetch lista ───────────────────────────────────────────────────────────

  const fetchList = useCallback(async () => {
    try {
      const params = new URLSearchParams({ assigned: tab });
      if (search) params.append('search', search);
      const { data } = await api.get(`/conversations?${params}`);
      setConvs(data);
    } catch (e) { console.error(e); }
    finally { setLL(false); }
  }, [tab, search]);

  useEffect(() => {
    setLL(true);
    fetchList();
    clearInterval(listTimer.current);
    listTimer.current = setInterval(fetchList, 5000);
    return () => clearInterval(listTimer.current);
  }, [fetchList]);

  // ── Fetch detalhe ─────────────────────────────────────────────────────────

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setLD(true);
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setDetail(data);
    } catch (e) { console.error(e); }
    finally { setLD(false); }
  }, []);

  useEffect(() => {
    clearInterval(detTimer.current);
    if (activeId) {
      fetchDetail(activeId);
      detTimer.current = setInterval(() => fetchDetail(activeId), 5000);
    } else {
      setDetail(null);
    }
    return () => clearInterval(detTimer.current);
  }, [activeId, fetchDetail]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages]);

  useEffect(() => {
    if (activeId) setTimeout(() => replyRef.current?.focus(), 50);
  }, [activeId]);

  // ── Ações ─────────────────────────────────────────────────────────────────

  async function assume() {
    await api.put(`/conversations/${activeId}/assume`);
    fetchDetail(activeId); fetchList();
  }

  async function release() {
    await api.put(`/conversations/${activeId}/release`);
    fetchDetail(activeId); fetchList();
  }

  async function close() {
    await api.put(`/conversations/${activeId}/close`);
    setActive(null); fetchList();
  }

  async function transfer() {
    if (!transferTo.trim()) return;
    await api.put(`/conversations/${activeId}/transfer`, { to_email: transferTo.trim() });
    setTransferModal(false); setTransferTo('');
    fetchDetail(activeId); fetchList();
  }

  async function resetLead() {
    const confirmed = await alertConfirmar('Resetar lead?', 'O histórico será apagado e o fluxo reiniciará do zero.');
    if (!confirmed) return;
    try {
      await api.post(`/conversations/${activeId}/reset`);
      setDetail(d => d ? { ...d, messages: [], current_step: 1, bot_active: true, assumed_by: null } : d);
      const div = document.createElement('div');
      div.textContent = 'Lead resetado com sucesso!';
      Object.assign(div.style, {
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        background: '#54ba4a', color: '#fff', padding: '10px 20px',
        borderRadius: 8, fontWeight: 600, fontSize: 13,
        boxShadow: '0 4px 12px rgba(0,0,0,.15)',
      });
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 3000);
      fetchList();
    } catch (e) {
      alertErro('Erro ao resetar', e.response?.data?.error || 'Tente novamente.');
    }
  }

  async function updateTemperature(temp) {
    if (!activeId || updatingTemp) return;
    setUpdatingTemp(true);
    try {
      await api.patch(`/conversations/${activeId}/temperature`, { temperature: temp });
      setDetail(d => d ? { ...d, temperature: temp } : d);
      setConvs(prev => prev.map(c => c.id === activeId ? { ...c, temperature: temp } : c));
    } catch (e) { alertErro('Erro', 'Não foi possível atualizar a temperatura'); }
    finally { setUpdatingTemp(false); }
  }

  async function sendMessage(e) {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/conversations/${activeId}/messages`, { text: text.trim() });
      setText('');
      fetchDetail(activeId);
    } catch (err) {
      alertErro('Erro ao enviar', err.response?.data?.error || 'Não foi possível enviar a mensagem');
    } finally {
      setSending(false);
    }
  }

  // ── Estado da conversa ativa ───────────────────────────────────────────────

  const isBot      = detail?.bot_active !== false;
  const isMine     = !isBot && detail?.assumed_by === userEmail;
  const isOthers   = !isBot && detail?.assumed_by && detail?.assumed_by !== userEmail;
  const messages   = Array.isArray(detail?.messages) ? detail.messages : [];
  const contactLabel = detail?.contact_name || detail?.contact_phone || '—';
  const newCount   = convs.filter(c => c.bot_active && !c.assumed_by).length;
  const detailColor = avatarColor(detail?.contact_name || detail?.contact_phone || '');

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumbs mainTitle='Conversas' parent='Topo Prospect' title='Conversas' />
      <Container fluid className='p-0'>
        <Row className='g-0' style={{ height: 'calc(100vh - 140px)' }}>

          {/* ── Coluna esquerda — lista ──────────────────────────────────────── */}
          <Col xs={12} md={4} lg={3} className='border-end d-flex flex-column bg-white' style={{ height: '100%', overflow: 'hidden' }}>

            {/* Busca */}
            <div className='p-2 border-bottom'>
              <div className='position-relative'>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                <Input
                  type='text'
                  placeholder='Buscar por nome ou número...'
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  bsSize='sm'
                  style={{ paddingLeft: 32, background: '#f8f9fa' }}
                />
              </div>
            </div>

            {/* Abas */}
            <div className='d-flex border-bottom'>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setActive(null); }}
                  className={`flex-fill py-2 border-0 bg-transparent small fw-medium ${tab === t.key ? 'text-primary border-bottom border-primary' : 'text-muted'}`}
                  style={{ borderBottom: tab === t.key ? '2px solid' : '2px solid transparent' }}
                >
                  {t.label}
                  {t.key === 'new' && newCount > 0 && (
                    <Badge color='primary' pill className='ms-1' style={{ fontSize: '11px' }}>{newCount}</Badge>
                  )}
                </button>
              ))}
            </div>

            {/* Lista */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingList ? (
                <div className='p-4 text-center text-muted d-flex align-items-center justify-content-center gap-2' style={{ fontSize: '0.75rem' }}>
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
                </div>
              ) : convs.length === 0 ? (
                <div className='p-4 text-center'>
                  <p className='text-muted mb-0' style={{ fontSize: '0.75rem' }}>Nenhuma conversa</p>
                  {tab === 'new' && (
                    <p className='text-muted mt-2 mb-0' style={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                      Nenhuma resposta recebida ainda. Inicie uma campanha para começar.
                    </p>
                  )}
                </div>
              ) : (
                convs.map(conv => {
                  const aColor  = avatarColor(conv.contact_name || conv.contact_phone || conv.id);
                  const isNew   = conv.bot_active && !conv.assumed_by;
                  const unread  = conv.unread_count || 0;
                  return (
                    <button
                      key={conv.id}
                      onClick={() => setActive(conv.id)}
                      className={`w-100 text-start p-3 border-0 border-bottom ${activeId === conv.id ? '' : 'bg-white'}`}
                      style={{
                        transition: 'background 0.15s',
                        background: activeId === conv.id ? aColor + '18' : '',
                        borderLeft: activeId === conv.id ? `3px solid ${aColor}` : '3px solid transparent',
                      }}
                    >
                      <div className='d-flex align-items-start gap-2'>
                        {/* Avatar */}
                        <div className='position-relative flex-shrink-0'>
                          <div className='rounded-circle d-flex align-items-center justify-content-center fw-bold'
                            style={{ width: 36, height: 36, fontSize: '0.7rem', background: aColor + '22', color: aColor }}>
                            {initials(conv.contact_name, conv.contact_phone)}
                          </div>
                          {isNew && (
                            <span style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 10, height: 10, borderRadius: '50%',
                              background: '#54ba4a', border: '2px solid #fff',
                              animation: 'pulse 1.5s infinite',
                            }} />
                          )}
                        </div>
                        <div className='flex-grow-1 min-w-0'>
                          <div className='d-flex align-items-center justify-content-between'>
                            <p className='mb-0 fw-semibold text-truncate' style={{ fontSize: '0.75rem' }}>
                              {conv.contact_name || conv.contact_phone}
                            </p>
                            <div className='d-flex align-items-center gap-1 flex-shrink-0 ms-1'>
                              {unread > 0 && (
                                <span className='badge rounded-pill d-flex align-items-center justify-content-center'
                                  style={{ background: aColor, fontSize: '11px', minWidth: 16, height: 16, padding: '0 4px' }}>
                                  {unread > 9 ? '9+' : unread}
                                </span>
                              )}
                              <span className='text-muted' style={{ fontSize: '0.7rem' }}>{timeAgo(conv.updated_at)}</span>
                            </div>
                          </div>
                          <p className='mb-0 text-muted text-truncate mt-1' style={{ fontSize: '0.7rem' }}>{lastMsg(conv.messages)}</p>
                          <div className='d-flex align-items-center gap-1 mt-1 flex-wrap'>
                            {conv.campaign_tag && (
                              <Badge color='secondary' pill style={{ fontSize: '11px' }}>{conv.campaign_tag}</Badge>
                            )}
                            {conv.temperature && conv.temperature !== 'frio' && (() => {
                              const tc = TEMP_CONFIG[conv.temperature];
                              return (
                                <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: 10,
                                  background: tc.bg, color: tc.color, border: `1px solid ${tc.border}` }}>
                                  {tc.label} {conv.temperature}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </Col>

          {/* ── Coluna direita — conversa ─────────────────────────────────────── */}
          <Col xs={12} md={8} lg={9} className='d-flex flex-column' style={{ height: '100%', overflow: 'hidden', background: '#f8f9fa' }}>
            {!activeId ? (
              <div className='flex-grow-1 d-flex align-items-center justify-content-center'>
                <div className='text-center text-muted' style={{ maxWidth: 280 }}>
                  <div style={{ fontSize: 40 }} className='mb-3'>💬</div>
                  <p className='fw-medium small mb-1'>Selecione uma conversa para visualizar</p>
                  <p className='text-muted' style={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                    As conversas aparecem aqui quando leads respondem aos disparos das campanhas.
                  </p>
                </div>
              </div>
            ) : loadingDet && !detail ? (
              <div className='flex-grow-1 d-flex align-items-center justify-content-center text-muted gap-2'>
                <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
              </div>
            ) : detail ? (
              <>
                {/* Header da conversa */}
                <div className='bg-white border-bottom px-4 py-3 d-flex align-items-center gap-3 flex-shrink-0'>
                  <div className='rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0'
                    style={{ width: 36, height: 36, fontSize: '0.8rem', background: detailColor + '22', color: detailColor }}>
                    {initials(detail.contact_name, detail.contact_phone)}
                  </div>
                  <div className='flex-grow-1 min-w-0'>
                    <p className='mb-0 fw-semibold small'>{contactLabel}</p>
                    <div className='d-flex align-items-center gap-2'>
                      <p className='mb-0 text-muted font-monospace' style={{ fontSize: '0.7rem' }}>{detail.contact_phone}</p>
                      {detail.campaign_tag && (
                        <Badge color='primary' pill style={{ fontSize: '11px' }}>{detail.campaign_tag}</Badge>
                      )}
                    </div>
                  </div>
                  {/* Termômetro */}
                  <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                    {Object.entries(TEMP_CONFIG).map(([key, tc]) => {
                      const active = (detail?.temperature || 'frio') === key;
                      return (
                        <button key={key} onClick={() => !isAdmin && updateTemperature(key)}
                          title={key.charAt(0).toUpperCase() + key.slice(1)}
                          style={{ border: `1px solid ${active ? tc.border : '#dee2e6'}`,
                            background: active ? tc.bg : '#fff', color: active ? tc.color : '#aaa',
                            borderRadius: 8, padding: '3px 8px', cursor: isAdmin ? 'default' : 'pointer',
                            fontSize: '0.7rem', fontWeight: active ? 600 : 400 }}>
                          {tc.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                    {!isAdmin && (
                      <>
                        <Button color='light' size='sm' onClick={() => setTransferModal(true)}>
                          ⇄ Transferir
                        </Button>
                        <Button color='light' size='sm' onClick={close}>
                          ✓ Concluir
                        </Button>
                      </>
                    )}
                    <Button color='outline-danger' size='sm' onClick={resetLead} title='Apaga o histórico e reinicia o fluxo do zero'>
                      ↺ Resetar
                    </Button>
                  </div>
                </div>

                {/* Banner de modo */}
                {isBot && (
                  <div className='bg-info bg-opacity-10 border-bottom border-info d-flex align-items-center justify-content-between px-4 py-2 flex-shrink-0'>
                    <span className='text-info small fw-medium'>🤖 Esta conversa está sendo conduzida pelo agente de IA ({detail.agent_id})</span>
                    {!isAdmin && <Button color='info' size='sm' onClick={assume}>Assumir conversa</Button>}
                  </div>
                )}
                {isMine && (
                  <div className='bg-success bg-opacity-10 border-bottom border-success d-flex align-items-center justify-content-between px-4 py-2 flex-shrink-0'>
                    <span className='text-success small fw-medium'>👤 Você está conduzindo esta conversa</span>
                    {!isAdmin && <Button color='success' size='sm' outline onClick={release}>🤖 Devolver para o bot</Button>}
                  </div>
                )}
                {isOthers && (
                  <div className='bg-warning bg-opacity-10 border-bottom border-warning d-flex align-items-center justify-content-between px-4 py-2 flex-shrink-0'>
                    <span className='text-warning small fw-medium'>👤 Conversa assumida por <strong>{detail.assumed_by}</strong></span>
                    {!isAdmin && <Button color='warning' size='sm' outline onClick={assume}>Transferir para mim</Button>}
                  </div>
                )}

                {/* Mensagens */}
                <div className='flex-grow-1 px-4 py-3 d-flex flex-column gap-2' style={{ overflowY: 'auto' }}>
                  {messages.length === 0 ? (
                    <div className='text-center text-muted py-4' style={{ fontSize: '0.75rem' }}>Sem mensagens registradas</div>
                  ) : (
                    messages.map((msg, i) => {
                      const isAgent = msg.role === 'assistant';
                      return (
                        <div key={i} className={`d-flex ${isAgent ? 'justify-content-end' : 'justify-content-start'}`}>
                          <div
                            className={`px-3 py-2 rounded-3 small`}
                            style={{
                              maxWidth: 360,
                              background: isAgent ? '#7366ff' : '#fff',
                              color: isAgent ? '#fff' : '#333',
                              border: isAgent ? 'none' : '1px solid #e9ecef',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: 1.5,
                            }}
                          >
                            {msg.type === 'media' && msg.media_url ? (
                              <div className='mb-1'>
                                {msg.media_type === 'photo' ? (
                                  <img src={msg.media_url} alt={msg.media_name || 'Mídia'}
                                    style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
                                ) : msg.media_type === 'video' ? (
                                  <video src={msg.media_url} controls style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
                                ) : msg.media_type === 'audio' ? (
                                  <audio src={msg.media_url} controls style={{ maxWidth: '100%' }} />
                                ) : (
                                  <p className='mb-0'>{msg.content}</p>
                                )}
                              </div>
                            ) : (
                              <p className='mb-1'>{msg.content}</p>
                            )}
                            <p className='mb-0 text-end' style={{ fontSize: '11px', opacity: 0.7 }}>
                              {formatMsgTime(msg.ts)}
                              {msg.by && <span className='ms-1'>· {msg.by.split('@')[0]}</span>}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEnd} />
                </div>

                {/* Campo de envio */}
                <form onSubmit={sendMessage} className='bg-white border-top px-3 py-2 d-flex align-items-end gap-2 flex-shrink-0'>
                  {isAdmin ? (
                    <p className='flex-grow-1 text-muted mb-0 py-2 px-3 bg-light rounded small fst-italic'>
                      Modo auditoria — faça login como tenant para responder
                    </p>
                  ) : !isMine ? (
                    <p className='flex-grow-1 text-muted mb-0 py-2 px-3 bg-light rounded small fst-italic'>
                      {isBot
                        ? 'Esta conversa precisa ser assumida para enviar mensagens'
                        : `Assumida por ${detail.assumed_by} — você não pode responder`}
                    </p>
                  ) : (
                    <>
                      <Input
                        type='textarea'
                        innerRef={replyRef}
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder='Digite sua mensagem... (Enter para enviar)'
                        rows={1}
                        style={{ resize: 'none', borderRadius: 12 }}
                      />
                      <Button
                        type='submit'
                        color='primary'
                        disabled={!text.trim() || sending}
                        style={{ borderRadius: 12, padding: '0.5rem 0.75rem', flexShrink: 0 }}
                      >
                        <Send size={16} />
                      </Button>
                    </>
                  )}
                </form>
              </>
            ) : null}
          </Col>
        </Row>
      </Container>

      {/* Modal transferir */}
      <Modal isOpen={transferModal} toggle={() => { setTransferModal(false); setTransferTo(''); }}>
        <ModalHeader toggle={() => { setTransferModal(false); setTransferTo(''); }}>
          Transferir conversa
        </ModalHeader>
        <ModalBody>
          <FormGroupField label='E-mail do destinatário'>
            <Input
              type='email'
              bsSize='sm'
              value={transferTo}
              onChange={e => setTransferTo(e.target.value)}
              placeholder='usuario@empresa.com'
            />
          </FormGroupField>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => { setTransferModal(false); setTransferTo(''); }}>Cancelar</Button>
          <Button color='primary' disabled={!transferTo.trim()} onClick={transfer}>Transferir</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function FormGroupField({ label, children }) {
  return (
    <div className='mb-2'>
      <label className='small fw-medium d-block mb-1'>{label}</label>
      {children}
    </div>
  );
}
